import type { EtapaDoKommo, EtapaSelecionada, PipelineDoKommo } from "@base/contracts";
import { Popover, PopoverContent, PopoverTrigger, cn } from "@base/ui";
import { Check, ChevronDown, ChevronRight, Search } from "lucide-react";
import { useMemo, useState } from "react";

/** Busca sem diferenciar maiuscula nem acento: "consulta" acha "Consulta realizada". */
const normalizar = (texto: string) =>
  texto
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();

const ROTULO_DO_TIPO: Readonly<Record<EtapaDoKommo["tipo"], string | null>> = {
  aberta: null,
  ganha: "Ganho",
  perdida: "Perda",
};

/** Texto do botao: "Etapa · Pipeline", ou `null` se a etapa nao existe mais. */
export function descreverEtapa(pipelines: readonly PipelineDoKommo[], valor: EtapaSelecionada | null): string | null {
  if (!valor) return null;
  const pipeline = pipelines.find((item) => item.id === valor.pipelineId);
  const etapa = pipeline?.etapas.find((item) => item.id === valor.etapaId);
  return pipeline && etapa ? `${etapa.nome} · ${pipeline.nome}` : null;
}

/**
 * Escolha de uma etapa do Kommo: lista com busca, com os funis (pipelines)
 * como ancoras recolhiveis. Abrir um funil mostra as etapas dele; digitar na
 * busca abre todos os funis que tem etapa correspondente.
 */
export function SeletorDeEtapa({
  rotulo,
  pipelines,
  valor,
  aoEscolher,
  desabilitado = false,
}: {
  readonly rotulo: string;
  readonly pipelines: readonly PipelineDoKommo[];
  readonly valor: EtapaSelecionada | null;
  readonly aoEscolher: (etapa: EtapaSelecionada) => void;
  readonly desabilitado?: boolean;
}) {
  const [aberto, setAberto] = useState(false);
  const [busca, setBusca] = useState("");
  const [expandidos, setExpandidos] = useState<ReadonlySet<number>>(() => new Set());
  const termo = normalizar(busca);
  const descricao = descreverEtapa(pipelines, valor);

  const visiveis = useMemo(
    () =>
      pipelines
        .map((pipeline) => ({
          pipeline,
          // Funil cujo nome bate mostra todas as etapas; senao, so as que batem.
          etapas:
            termo === "" || normalizar(pipeline.nome).includes(termo)
              ? pipeline.etapas
              : pipeline.etapas.filter((etapa) => normalizar(etapa.nome).includes(termo)),
        }))
        .filter((item) => item.etapas.length > 0),
    [pipelines, termo],
  );

  function abrir(proximo: boolean) {
    setAberto(proximo);
    if (!proximo) return;
    setBusca("");
    // Ao abrir, o funil da etapa escolhida (ou o principal) ja vem aberto.
    const inicial = valor?.pipelineId ?? pipelines.find((pipeline) => pipeline.principal)?.id;
    setExpandidos(new Set(inicial === undefined ? [] : [inicial]));
  }

  function alternar(pipelineId: number) {
    setExpandidos((atuais) => {
      const proximos = new Set(atuais);
      if (proximos.has(pipelineId)) proximos.delete(pipelineId);
      else proximos.add(pipelineId);
      return proximos;
    });
  }

  function escolher(etapa: EtapaSelecionada) {
    aoEscolher(etapa);
    setAberto(false);
  }

  const primeiraVisivel = visiveis[0];

  return (
    <Popover open={aberto} onOpenChange={abrir}>
      <PopoverTrigger asChild disabled={desabilitado}>
        <button
          type="button"
          aria-label={`${rotulo}: ${descricao ?? "nenhuma etapa escolhida"}`}
          className={cn(
            "flex h-11 w-full items-center justify-between gap-3 rounded-lg border border-input bg-card px-3.5 text-left text-sm shadow-sm transition-colors outline-none",
            "hover:border-input/80 focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-primary/25",
            "disabled:cursor-not-allowed disabled:opacity-50",
            !descricao && "text-muted-foreground",
          )}
        >
          <span className="truncate">{descricao ?? (valor ? "Etapa não existe mais no Kommo" : "Escolher etapa")}</span>
          <ChevronDown className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-(--radix-popover-trigger-width) min-w-72 p-0">
        <div className="flex items-center gap-2 border-b px-3">
          <Search className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          <input
            autoFocus
            value={busca}
            onChange={(evento) => setBusca(evento.target.value)}
            onKeyDown={(evento) => {
              // Enter com busca escolhe a primeira etapa encontrada.
              const etapa = primeiraVisivel?.etapas[0];
              if (evento.key === "Enter" && termo !== "" && primeiraVisivel && etapa) {
                evento.preventDefault();
                escolher({ pipelineId: primeiraVisivel.pipeline.id, etapaId: etapa.id });
              }
            }}
            placeholder="Buscar funil ou etapa"
            aria-label="Buscar funil ou etapa"
            className="h-11 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
          />
        </div>

        <div className="max-h-80 overflow-y-auto p-1.5" aria-label={`Etapas do Kommo para ${rotulo}`} role="group">
          {visiveis.length === 0 ? (
            <p className="px-3 py-6 text-center text-sm text-muted-foreground">Nenhuma etapa encontrada.</p>
          ) : (
            visiveis.map(({ pipeline, etapas }) => {
              const expandido = termo !== "" || expandidos.has(pipeline.id);
              return (
                <div key={pipeline.id}>
                  <button
                    type="button"
                    aria-expanded={expandido}
                    onClick={() => alternar(pipeline.id)}
                    className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-sm font-medium hover:bg-accent hover:text-accent-foreground"
                  >
                    <ChevronRight
                      className={cn("size-4 shrink-0 text-muted-foreground transition-transform", expandido && "rotate-90")}
                      aria-hidden="true"
                    />
                    <span className="flex-1 truncate">{pipeline.nome}</span>
                    {pipeline.principal ? (
                      <span className="rounded-full bg-accent px-2 py-0.5 text-[10px] font-semibold text-accent-foreground">
                        Principal
                      </span>
                    ) : null}
                    <span className="text-xs text-muted-foreground tabular-nums">{pipeline.etapas.length}</span>
                  </button>

                  {expandido ? (
                    <ul aria-label={`Etapas de ${pipeline.nome}`} className="mb-1 ml-4 flex flex-col border-l pl-2">
                      {etapas.map((etapa) => {
                        const escolhida = valor?.pipelineId === pipeline.id && valor.etapaId === etapa.id;
                        const tipo = ROTULO_DO_TIPO[etapa.tipo];
                        return (
                          <li key={etapa.id}>
                            <button
                              type="button"
                              aria-pressed={escolhida}
                              onClick={() => escolher({ pipelineId: pipeline.id, etapaId: etapa.id })}
                              className={cn(
                                "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-accent hover:text-accent-foreground",
                                escolhida && "font-medium text-primary",
                              )}
                            >
                              <span className="flex-1 truncate">{etapa.nome}</span>
                              {tipo ? (
                                <span
                                  className={cn(
                                    "rounded-full px-2 py-0.5 text-[10px] font-semibold",
                                    etapa.tipo === "ganha" ? "bg-success/12 text-success" : "bg-destructive/12 text-destructive",
                                  )}
                                >
                                  {tipo}
                                </span>
                              ) : null}
                              <Check className={cn("size-4 shrink-0", escolhida ? "opacity-100" : "opacity-0")} aria-hidden="true" />
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  ) : null}
                </div>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
